import { LanguageIcon } from '@bravemove/design-tokens/icons';
import { Select, type SelectOption } from '../Select/Select';
import { cn } from '../../utils/cn';

export type LanguageCode = 'en' | 'ar' | 'fr';

export interface LanguageSwitcherProps {
  readonly value: LanguageCode;
  readonly onChange: (code: LanguageCode) => void;
  readonly variant?: 'compact' | 'dropdown';
  readonly className?: string;
  readonly availableLanguages?: readonly LanguageCode[];
}

const LANGUAGE_LABEL: Record<LanguageCode, string> = {
  en: 'English',
  ar: 'العربية',
  fr: 'Français',
};

const COMPACT: Record<LanguageCode, string> = {
  en: 'EN',
  ar: 'AR',
  fr: 'FR',
};

const DEFAULT_LANGS: readonly LanguageCode[] = ['en', 'ar', 'fr'];

export function LanguageSwitcher({
  value,
  onChange,
  variant = 'compact',
  className,
  availableLanguages = DEFAULT_LANGS,
}: LanguageSwitcherProps) {
  if (variant === 'dropdown') {
    const options: SelectOption[] = availableLanguages.map((code) => ({
      value: code,
      label: LANGUAGE_LABEL[code],
    }));
    return (
      <Select
        size="sm"
        options={options}
        value={value}
        onChange={(e) => onChange(e.currentTarget.value as LanguageCode)}
        aria-label="Language"
        className={className}
      />
    );
  }
  return (
    <div
      role="group"
      aria-label="Language"
      className={cn(
        'inline-flex items-center gap-1 rounded-md p-1 bg-surface-2 border border-hairline',
        className,
      )}
    >
      <span className="text-ink-3 ps-1" aria-hidden="true">
        <LanguageIcon size={14} color="currentColor" />
      </span>
      {availableLanguages.map((code) => {
        const active = code === value;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            aria-pressed={active}
            className={cn(
              'h-6 px-1.5 inline-flex items-center justify-center rounded-sm text-[10.5px] font-bold uppercase tracking-wider',
              'transition-colors duration-[120ms] focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40',
              active ? 'bg-ink text-bg' : 'text-ink-2 hover:text-ink',
            )}
          >
            {COMPACT[code]}
          </button>
        );
      })}
    </div>
  );
}
